import React, { useEffect, useState } from 'react';
import apiService from '../services/api';
import styles from './SystemConfig.module.css';

const SystemConfig = () => {
  const [autoTaskEnabled, setAutoTaskEnabled] = useState(false);
  const [cronTime, setCronTime] = useState('00:00');
  const [statusObj, setStatusObj] = useState({ code: '', desc: '', last_run_time: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchConfig();
    fetchStatus();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await apiService.getSystemConfig();
      setAutoTaskEnabled(res.data.auto_award_enabled);
      setCronTime(res.data.cron_time || '00:00');
    } catch (e) {
      setError('获取配置失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await apiService.getAutoTaskStatus();
      if (res.data && typeof res.data === 'object') {
        setStatusObj({
          code: res.data.code || '',
          desc: res.data.desc || ''
        });
      } else {
        setStatusObj({ code: '', desc: '未知状态' });
      }
    } catch (e) {
      setStatusObj({ code: '', desc: '获取状态失败' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await apiService.setSystemConfig({
        auto_award_enabled: autoTaskEnabled,
        cron_time: cronTime
      });
      fetchStatus();
    } catch (e) {
      setError('保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container" style={{maxWidth: 600, margin: '0 auto'}}>
      <div style={{display:'flex',alignItems:'center',marginBottom:'1.2rem'}}>
        <button
          type="button"
          onClick={() => window.history.back()}
          style={{
            border: 'none',
            background: 'linear-gradient(135deg,#f7f8fa 70%,#eaf2f6 100%)',
            color: '#18808e',
            borderRadius: '50%',
            padding: '0.65em',
            marginRight: '1.3rem',
            fontSize: '1.32em',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(24,128,142,0.11)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'box-shadow 0.18s,background 0.18s',
            outline: 'none',
            minWidth: '2.3em',
            minHeight: '2.3em',
          }}
          onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(24,128,142,0.18)'}
          onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(24,128,142,0.11)'}
          aria-label="返回"
        >
          <svg width="1.4em" height="1.4em" viewBox="0 0 24 24" fill="none" stroke="#18808e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h2 className="mb-4 text-center" style={{fontWeight:700,letterSpacing:'1px',marginBottom:0,flex:1}}>系统配置</h2>
      </div>
      <div className={styles['config-card']}>
        <div className={styles['config-title']} style={{textAlign:'left',fontWeight:600,fontSize:'1.18rem',marginBottom:'1.2rem'}}>自动发放活动积分</div>
        <div className={styles['switch-row']}>
          <input
            className="form-check-input"
            type="checkbox"
            id="autoTaskSwitch"
            checked={autoTaskEnabled}
            onChange={async e => {
              const checked = e.target.checked;
              setAutoTaskEnabled(checked);
              setSaving(true);
              setError('');
              try {
                await apiService.setSystemConfig({
                  auto_award_enabled: checked,
                  cron_time: cronTime
                });
                fetchStatus();
              } catch (err) {
                setError('保存失败');
                setAutoTaskEnabled(!checked); // 回滚
              } finally {
                setSaving(false);
              }
            }}
            style={{transform:'scale(1.25)'}}
            disabled={saving || loading}
          />
          <label className={styles['switch-label']} htmlFor="autoTaskSwitch">
            启用自动发放功能
          </label>
        </div>
        <div className={styles['time-row']}>
          <label htmlFor="cronTime" className={styles['time-label']}>执行时间</label>
          <input
            type="time"
            className="form-control"
            id="cronTime"
            value={cronTime}
            onChange={async e => {
              const val = e.target.value;
              setCronTime(val);
              setSaving(true);
              setError('');
              try {
                await apiService.setSystemConfig({
                  auto_award_enabled: autoTaskEnabled,
                  cron_time: val
                });
                fetchStatus();
              } catch (err) {
                setError('保存失败');
                // 不回滚时间，便于用户修正
              } finally {
                setSaving(false);
              }
            }}
            disabled={!autoTaskEnabled || saving || loading}
            style={{maxWidth:160}}
          />
          <span className="ms-2 text-muted" style={{fontSize:'0.97em'}}>每天定时</span>
        </div>
        {error && <div className="alert alert-danger mt-3">{error}</div>}
        <div className={styles['status-section']}>
          <div className={styles['status-title']}>自动发放积分脚本状态</div>
          <div className={styles['status-info']}>
            {statusObj.code === 'running' && (
              <span style={{color:'#28a745',fontWeight:500}}>脚本正在运行，积分将自动发放</span>
            )}
            {statusObj.code === 'ready' && (
              <span style={{color:'#007bff'}}>计划任务已注册，等待定时自动执行</span>
            )}
            {statusObj.code === 'stopped' && (
              <span style={{color:'#fd7e14'}}>计划任务注册但脚本当前未运行，等待下次定时</span>
            )}
            {statusObj.code === 'none' && (
              <span style={{color:'#dc3545'}}>未设置自动发放任务，积分不会自动发放</span>
            )}
            {statusObj.code === 'unknown' && (
              <span style={{color:'#6c757d'}}>状态未知，请联系管理员</span>
            )}
            {!['running','ready','stopped','none','unknown'].includes(statusObj.code) && statusObj.desc && (
              <div style={{fontSize:'0.98em',marginTop:'0.2em',color:'#666'}}>{statusObj.desc}</div>
            )}
            {statusObj.last_run_time && (
              <div style={{fontSize:'0.95em',marginTop:'0.6em',color:'#666'}}>
                上次自动执行时间：{statusObj.last_run_time}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemConfig;
